import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Drawer } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faBookOpen } from '@fortawesome/free-solid-svg-icons';

import { FunctionalRootState } from '@share/utils';

import type { IAccountGuide } from '../../guides';
import type { IAccountSection } from '../../types';
import { getAccountSectionPath, getHubThemeStyle } from '../../utils';

import styles from './styles.module.scss';

// Below this width the guide drawer takes the full screen instead of a fixed 480px panel.
const PHONE_QUERY = '(max-width: 575px)';
const DRAWER_WIDTH = 480;

const useIsPhone = (): boolean => {
  const [isPhone, setIsPhone] = useState(() => typeof window !== 'undefined' && window.matchMedia(PHONE_QUERY).matches);

  useEffect(() => {
    const query = window.matchMedia(PHONE_QUERY);
    const handleChange = (event: MediaQueryListEvent) => setIsPhone(event.matches);
    query.addEventListener('change', handleChange);
    return () => query.removeEventListener('change', handleChange);
  }, []);

  return isPhone;
};

interface IProps {
  guides: IAccountGuide[];
  sections: IAccountSection[];
  accountUsername: string;
}

/** "Help and guides" cards; each opens a short step-by-step guide in a side drawer. */
export const AccountGuides = ({ guides, sections, accountUsername }: IProps): React.ReactElement | null => {
  const intl = useIntl();
  const [openId, setOpenId] = useState<IAccountGuide['id'] | null>(null);
  const account = useSelector((state: FunctionalRootState) => state.loginStore.account);
  const isPhone = useIsPhone();

  if (!guides.length) {
    return null;
  }

  const text = (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values);
  const open = guides.find(guide => guide.id === openId) || null;
  const actionSection = open?.actionSection ? sections.find(section => section.id === open.actionSection) : undefined;

  return (
    <section aria-labelledby="account-hub-guides-title">
      <h2 id="account-hub-guides-title" className={styles.label}>{text('my.account.hub.guides.title')}</h2>
      <ul className={styles.guides}>
        {guides.map(guide => (
          <li key={guide.id}>
            <button type="button" className={styles.guide} onClick={() => setOpenId(guide.id)}>
              <span className={styles.guideIcon} aria-hidden="true"><FontAwesomeIcon icon={faBookOpen} /></span>
              <span className={styles.guideText}>
                <span className={styles.guideTitle}>{text(`my.account.hub.guide.${guide.id}.title`)}</span>
                <span className={styles.guideSummary}>{text(`my.account.hub.guide.${guide.id}.summary`)}</span>
              </span>
              <span className={styles.guideMeta}>{text('my.account.hub.guides.read_time', { minutes: guide.readMinutes })}</span>
            </button>
          </li>
        ))}
      </ul>

      <Drawer
        open={!!open}
        onClose={() => setOpenId(null)}
        placement="right"
        width={isPhone ? '100%' : DRAWER_WIDTH}
        zIndex={4100}
        title={open ? text(`my.account.hub.guide.${open.id}.title`) : ''}
        className={styles.guideDrawer}
        style={getHubThemeStyle(account)}
        destroyOnClose
      >
        {open ? (
          <article className={styles.guideArticle}>
            <p className={styles.guideLead}>{text(`my.account.hub.guide.${open.id}.summary`)}</p>
            <ol className={styles.guideSteps}>
              {Array.from({ length: open.steps }, (_, index) => (
                <li key={index}>{text(`my.account.hub.guide.${open.id}.step${index + 1}`)}</li>
              ))}
            </ol>
            {actionSection ? (
              <Link
                to={getAccountSectionPath(accountUsername, actionSection)}
                className={styles.guideAction}
                onClick={() => setOpenId(null)}
              >
                {text(`my.account.hub.guide.${open.id}.action`)}
                <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
              </Link>
            ) : null}
          </article>
        ) : null}
      </Drawer>
    </section>
  );
};
